import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from '@uzurv/afp/button';
import { LabelCardModule } from '@uzurv/afp/label-card';
import { PersonSuspensionDetail } from '@uzurv/clara';
import { DateFormatConstants, isNullOrUndefined, TimeZoneUtil } from '@uzurv/core';
import {
  PersonSuspensionStatus,
  PersonSuspensionStatusChipComponent
} from '../person-suspension-status-chip/person-suspension-status-chip.component';

@Component({
  selector: 'app-person-suspension-card',
  templateUrl: './person-suspension-card.component.html',
  styleUrls: ['./person-suspension-card.component.scss'],
  imports: [CommonModule, ButtonModule, LabelCardModule, PersonSuspensionStatusChipComponent],
  standalone: true
})
export class PersonSuspensionCardComponent {
  @Input() personSuspensionDetail: PersonSuspensionDetail;
  @Input() hasRemovePermission = false;
  @Output() removeSuspension = new EventEmitter<PersonSuspensionDetail>();

  getStartDate(): string {
    return TimeZoneUtil.formatLocalTimeZoneDate(this.personSuspensionDetail?.effectiveAt, DateFormatConstants.M_D_YY_AT_TIME_WITH_TIMEZONE);
  }

  getEndDate(): string | null {
    if (isNullOrUndefined(this.personSuspensionDetail)) {
      return '';
    }

    if (this.isSuspensionPermanent()) {
      return 'Permanent';
    }

    if (this.isSuspensionIndefinite()) {
      return 'Indefinite';
    }

    const endDate = this.calculateEndDate();
    if (isNullOrUndefined(endDate)) {
      return '';
    }

    return TimeZoneUtil.formatLocalTimeZoneDate(endDate, DateFormatConstants.M_D_YY_AT_TIME_WITH_TIMEZONE);
  }

  getSuspensionStatus(): PersonSuspensionStatus {
    if (isNullOrUndefined(this.personSuspensionDetail) || isNullOrUndefined(this.personSuspensionDetail?.effectiveAt)) {
      return null;
    }

    if (this.isSuspensionPermanent() && this.personSuspensionDetail.active) {
      return PersonSuspensionStatus.EFFECTIVE_PERMANENTLY;
    }

    if (!this.personSuspensionDetail.active && !this.personSuspensionDetail.expired) {
      return PersonSuspensionStatus.REMOVED;
    }

    if (this.personSuspensionDetail.effective && !this.personSuspensionDetail.expired) {
      return PersonSuspensionStatus.EFFECTIVE;
    }

    if (this.personSuspensionDetail.expired) {
      return PersonSuspensionStatus.EXPIRED;
    }

    if (this.personSuspensionDetail.active && !this.personSuspensionDetail.effective && !this.personSuspensionDetail.expired) {
      return PersonSuspensionStatus.EFFECTIVE_ON;
    }

    return null;
  }

  getDeactivatedByDisplay(): string {
    if (!this.personSuspensionDetail?.deactivatedBy || !this.personSuspensionDetail?.deactivatedAt) {
      return '';
    }
    const formattedDateTime = TimeZoneUtil.formatLocalTimeZoneDate(
      this.personSuspensionDetail.deactivatedAt,
      DateFormatConstants.M_D_YY_AT_TIME_WITH_TIMEZONE
    );
    return `${this.personSuspensionDetail.deactivatedBy} - ${formattedDateTime}`;
  }

  getCreatedByDisplay(): string {
    if (!this.personSuspensionDetail?.createdBy || !this.personSuspensionDetail?.createdDate) {
      return '';
    }
    const formattedDateTime = TimeZoneUtil.formatLocalTimeZoneDate(
      this.personSuspensionDetail.createdDate,
      DateFormatConstants.M_D_YY_AT_TIME_WITH_TIMEZONE
    );
    return `${this.personSuspensionDetail.createdBy} on ${formattedDateTime}`;
  }

  handleRemoveSuspension(): void {
    this.removeSuspension.emit(this.personSuspensionDetail);
  }

  private isSuspensionPermanent(): boolean {
    return this.personSuspensionDetail?.suspensionReason?.permanent;
  }

  private isSuspensionIndefinite(): boolean {
    return !this.personSuspensionDetail?.suspensionReason?.permanent && isNullOrUndefined(this.personSuspensionDetail?.durationHours);
  }

  private calculateEndDate(): Date | null {
    if (isNullOrUndefined(this.personSuspensionDetail?.effectiveAt) || isNullOrUndefined(this.personSuspensionDetail?.durationHours)) {
      return null;
    }
    return new Date(this.personSuspensionDetail.effectiveAt.getTime() + this.personSuspensionDetail.durationHours * 60 * 60 * 1000);
  }
}
